import { createContext, useContext, type ReactNode } from 'react';

const SegmentSurfaceContext = createContext<string | undefined>(undefined);

interface SegmentSurfaceProviderProps {
  surface: string;
  children: ReactNode;
}
/**
 * Provides a surface name to descendant components for Segment analytics tracking.
 * Consumers read it via {@link useSegmentSurface}.
 *
 * The `surface` value should be a static string that does not change after mount.
 * If the value becomes dynamic or is changed to an object, every consumer in the
 * subtree will re-render on each change — keep this in mind before extending.
 *
 * Providers can be nested; consumers always receive the value from the nearest
 * ancestor provider.
 */
export function SegmentSurfaceProvider({
  surface,
  children,
}: SegmentSurfaceProviderProps) {
  return (
    <SegmentSurfaceContext.Provider value={surface}>
      {children}
    </SegmentSurfaceContext.Provider>
  );
}
/**
 * Returns the surface name set by the nearest {@link SegmentSurfaceProvider},
 * or `undefined` if no provider exists above this component.
 */
export function useSegmentSurface(): string | undefined {
  return useContext(SegmentSurfaceContext);
}
